import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Button,
  TextField,
  Typography,
  CircularProgress
} from '@material-ui/core';
import useCountdown from '../../hooks/useCountdown';
import { REPLAY_MASK } from "../../utils/replay-mask";

// Email-ownership verification: a 6-digit code sent to the address captured in
// the profile-completion step. Auth is already complete when this renders —
// the step only proves the email before the user enters the app. Mirrors
// OTPInput's interaction (auto-advance, paste, auto-submit, resend cooldown).
const EmailVerify = ({
  email,
  onVerifyEmailOTP,
  onResendEmailOTP,
  onChangeEmail,
  loading,
  error,
  remainingAttempts = 3,
  resendCooldown = 60
}) => {
  const [otp, setOtp] = useState(['', '', '', '', '', '']);
  const inputRefs = useRef([]);
  const { seconds: resendSeconds, reset: resetResendTimer } = useCountdown(resendCooldown);

  useEffect(() => {
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  }, []);

  useEffect(() => {
    // Clear the code on error so the retry starts clean.
    if (error) {
      setOtp(['', '', '', '', '', '']);
      if (inputRefs.current[0]) {
        inputRefs.current[0].focus();
      }
    }
  }, [error]);

  const handleChange = (index, value) => {
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    if (value && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    if (newOtp.every(digit => digit !== '') && index === 5) {
      handleVerify(newOtp.join(''));
    }
  };

  const handleKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData('text/plain').trim();

    if (/^\d{6}$/.test(pastedData)) {
      setOtp(pastedData.split(''));
      inputRefs.current[5]?.focus();
      handleVerify(pastedData);
    }
  };

  const handleVerify = (otpCode = null) => {
    const otpValue = otpCode || otp.join('');

    if (otpValue.length !== 6) {
      return;
    }

    onVerifyEmailOTP({ email, otp: otpValue });
  };

  const handleResend = () => {
    setOtp(['', '', '', '', '', '']);
    resetResendTimer(resendCooldown);
    onResendEmailOTP({ email });
    if (inputRefs.current[0]) {
      inputRefs.current[0].focus();
    }
  };

  const isVerifyDisabled = otp.some(digit => digit === '') || loading;
  const isResendDisabled = resendSeconds > 0 || loading;

  return (
    <Box className="email-verify-container-wrapper" data-testid="email-verify">
      <Typography variant="h6" style={{ marginBottom: '10px', textAlign: 'center' }}>
        Verify Your Email
      </Typography>

      <Typography variant="body2" style={{ marginBottom: '25px', textAlign: 'center', color: '#666' }}>
        {/* data-hj-suppress: /login is allow-listed. Dark behind
            REACT_APP_QLEARN_VERIFIED_EMAIL today — which is exactly why it lands
            now, since flipping a runtime flag gets no code review. */}
        Enter the 6-digit code sent to <strong className="verify-email-address" {...REPLAY_MASK}>{email}</strong>
      </Typography>

      <Box className="otp-input-container" style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '15px' }}>
        {otp.map((digit, index) => (
          <TextField
            key={index}
            inputRef={(ref) => (inputRefs.current[index] = ref)}
            variant="outlined"
            value={digit}
            onChange={(e) => handleChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            onPaste={handlePaste}
            inputProps={{
              maxLength: 1,
              style: { textAlign: 'center', fontSize: '24px', padding: '12px' },
              inputMode: 'numeric',
              pattern: '[0-9]*',
              'aria-label': `Email verification digit ${index + 1}`
            }}
            className="otp-digit"
            style={{ width: '45px' }}
            error={!!error}
          />
        ))}
      </Box>

      {error && (
        <Box className="error-message" {...REPLAY_MASK} style={{ marginBottom: '10px', color: '#d32f2f', fontSize: '14px', textAlign: 'center' }}>
          {error}
          {remainingAttempts !== undefined && remainingAttempts < 3 && (
            <Box className={`attempts-remaining ${remainingAttempts === 1 ? 'critical' : ''}`} style={{ marginTop: '5px', fontSize: '13px' }}>
              {remainingAttempts} {remainingAttempts === 1 ? 'attempt' : 'attempts'} remaining
            </Box>
          )}
        </Box>
      )}

      <Button
        fullWidth
        variant="contained"
        color="primary"
        onClick={() => handleVerify()}
        disabled={isVerifyDisabled}
        className="verify-email-otp-btn"
        style={{ marginBottom: '15px' }}
      >
        {loading ? (
          <>
            Verifying <CircularProgress size={20} style={{ marginLeft: '10px', color: '#fff' }} />
          </>
        ) : (
          'Verify Email'
        )}
      </Button>

      <Box style={{ textAlign: 'center' }}>
        {resendSeconds > 0 ? (
          <Typography variant="body2" className="countdown-timer" style={{ color: '#666' }}>
            Resend code in <strong>{resendSeconds}s</strong>
          </Typography>
        ) : (
          <Button
            color="secondary"
            onClick={handleResend}
            disabled={isResendDisabled}
            style={{ textTransform: 'none' }}
          >
            Didn't receive the code? Resend
          </Button>
        )}
      </Box>

      <Box style={{ textAlign: 'center' }}>
        <Button
          onClick={onChangeEmail}
          data-testid="change-email"
          style={{ textTransform: 'none', fontSize: '13px', color: '#666' }}
        >
          Wrong address? Change email
        </Button>
      </Box>
    </Box>
  );
};

export default EmailVerify;
